<template>
  <div class="backup-container">
    <header class="backup-header">
      <div class="header-content">
        <div class="header-text">
          <h1>Tenant Backups</h1>
          <p class="header-subtitle">
            Manage and restore configuration backups for your tenants
          </p>
        </div>
        <div v-if="selectedClient" class="header-quick-stats">
          <div class="quick-stat">
            <span class="quick-stat-value">{{ backups.length }}</span>
            <span class="quick-stat-label">Total Backups</span>
          </div>
          <div class="quick-stat">
            <span class="quick-stat-value">{{ getBackupStatus() }}</span>
            <span class="quick-stat-label">Status</span>
          </div>
        </div>
      </div>
    </header>

    <div class="backup-content">
      <!-- Client Selection and Actions Row -->
      <div class="top-controls-row">
        <!-- Client Selection -->
        <div class="selection-section">
          <div class="section-header">
            <h2>Select Tenant</h2>
          </div>
          <div class="form-group">
            <label for="client-select"
              >Choose a tenant to manage backups:</label
            >
            <select
              id="client-select"
              v-model="selectedClient"
              @change="loadClientBackups"
              class="client-select"
            >
              <option value="">-- Select Tenant --</option>
              <option v-for="client in clients" :key="client" :value="client">
                {{ client }}
              </option>
            </select>
          </div>
        </div>

        <!-- Actions Section -->
        <div v-if="selectedClient" class="backup-actions">
          <div class="section-header">
            <h3>Backup Actions</h3>
          </div>
          <div class="action-buttons">
            <button
              @click="triggerManualBackup"
              :disabled="isBackingUp"
              class="btn btn-primary"
            >
              <span v-if="isBackingUp">🔄 Backing up...</span>
              <span v-else>💾 Manual Backup</span>
            </button>
            <button @click="configureSchedule" class="btn btn-secondary">
              ⏰ Configure Schedule
            </button>
            <button @click="refreshBackups" class="btn btn-secondary">
              🔄 Refresh
            </button>
          </div>
        </div>
      </div>

      <!-- Backup Stats -->
      <div v-if="selectedClient && backups.length" class="backup-stats">
        <div class="stat-card">
          <div class="stat-icon">📅</div>
          <div class="stat-content">
            <div class="stat-value">{{ getLatestBackupDate() }}</div>
            <div class="stat-label">Latest Backup</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon">📦</div>
          <div class="stat-content">
            <div class="stat-value">{{ backups.length }}</div>
            <div class="stat-label">Total Backups</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon">💾</div>
          <div class="stat-content">
            <div class="stat-value">{{ getStorageSize() }}</div>
            <div class="stat-label">Storage Used</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon">✅</div>
          <div class="stat-content">
            <div class="stat-value">{{ getBackupStatus() }}</div>
            <div class="stat-label">Status</div>
          </div>
        </div>
      </div>

      <!-- Backups Table -->
      <div v-if="selectedClient" class="backup-table-section">
        <div class="table-header">
          <h3>Backup History</h3>
          <div class="table-filters">
            <input
              v-model="searchFilter"
              type="text"
              placeholder="Search backups..."
              class="search-input"
            />
            <select v-model="dateFilter" class="filter-select">
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Past Week</option>
              <option value="month">Past Month</option>
            </select>
          </div>
        </div>

        <div v-if="isLoading" class="loading-state">
          <div class="loader"></div>
          <span>Loading backups...</span>
        </div>

        <div v-else-if="!backups.length" class="empty-state">
          <span>📭 No backups found for this tenant</span>
        </div>

        <div v-else class="backup-table-wrapper">
          <table class="backup-table">
            <thead>
              <tr>
                <th>Date & Time</th>
                <th>Backup Tag</th>
                <th>Type</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="backup in paginatedBackups"
                :key="backup.tagName"
                class="backup-row"
              >
                <td>
                  <div class="date-cell">
                    <span class="date-primary">{{
                      formatDate(backup.date)
                    }}</span>
                    <span class="date-secondary">{{
                      formatTime(backup.date)
                    }}</span>
                  </div>
                </td>
                <td>
                  <span class="tag-name">{{ backup.tagName }}</span>
                </td>
                <td>
                  <span
                    :class="[
                      'backup-type',
                      backup.isScheduled ? 'scheduled' : 'manual',
                    ]"
                  >
                    {{ backup.isScheduled ? "Scheduled" : "Manual" }}
                  </span>
                </td>
                <td>
                  <span class="status-badge status-success">✅ Success</span>
                </td>
                <td>
                  <div class="action-buttons">
                    <button @click="viewBackup(backup)" class="action-btn">
                      👁️ View
                    </button>
                    <button
                      @click="compareWithCurrent(backup)"
                      class="action-btn"
                    >
                      🔍 Compare
                    </button>
                    <button
                      @click="restoreBackup(backup)"
                      class="action-btn restore"
                    >
                      ♻️ Restore
                    </button>
                    <button @click="downloadBackup(backup)" class="action-btn">
                      ⬇️ Download
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Pagination -->
          <div v-if="totalPages > 1" class="pagination">
            <button
              @click="currentPage--"
              :disabled="currentPage === 1"
              class="page-btn"
            >
              Previous
            </button>
            <span class="page-info"
              >Page {{ currentPage }} of {{ totalPages }}</span
            >
            <button
              @click="currentPage++"
              :disabled="currentPage === totalPages"
              class="page-btn"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <!-- Backup Details Modal -->
      <transition name="modal">
        <div
          v-if="showBackupDetails"
          class="modal-overlay"
          @click.self="showBackupDetails = false"
        >
          <div class="modal-content">
            <div class="modal-header">
              <h2>Backup Details</h2>
              <button @click="showBackupDetails = false" class="close-button">
                ×
              </button>
            </div>
            <div class="modal-body">
              <div class="detail-section">
                <h3>Backup Information</h3>
                <div class="detail-grid">
                  <div class="detail-item">
                    <label>Tag Name</label>
                    <code>{{ currentBackup?.tagName }}</code>
                  </div>
                  <div class="detail-item">
                    <label>Created On</label>
                    <span>{{ formatDate(currentBackup?.date) }}</span>
                  </div>
                  <div class="detail-item">
                    <label>Type</label>
                    <span>{{
                      currentBackup?.isScheduled ? "Scheduled" : "Manual"
                    }}</span>
                  </div>
                  <div class="detail-item">
                    <label>Size</label>
                    <span>2.3 MB</span>
                  </div>
                </div>
              </div>

              <div class="detail-section">
                <h3>Configuration Content</h3>
                <pre class="json-preview">{{ backupContent }}</pre>
              </div>
            </div>
            <div class="modal-footer">
              <button
                @click="showBackupDetails = false"
                class="btn btn-secondary"
              >
                Close
              </button>
              <button
                @click="restoreBackup(currentBackup)"
                class="btn btn-primary"
              >
                Restore This Backup
              </button>
            </div>
          </div>
        </div>
      </transition>

      <!-- Comparison Modal -->
      <transition name="modal">
        <div
          v-if="showComparison"
          class="modal-overlay"
          @click.self="closeComparison"
        >
          <div class="modal-content modal-wide">
            <div class="modal-header">
              <h2>Configuration Comparison</h2>
              <button @click="closeComparison" class="close-button">×</button>
            </div>
            <div class="modal-body">
              <div class="comparison-header">
                <div class="comparison-info">
                  <span class="comparison-label">Comparing:</span>
                  <code class="tag-name">{{ currentBackup?.tagName }}</code>
                  <span class="comparison-vs">vs</span>
                  <span class="comparison-current">Current Configuration</span>
                </div>
                <div class="comparison-stats">
                  <span class="stat-item changes">
                    🔄 {{ getTotalChanges() }} changes
                  </span>
                  <span class="stat-item additions">
                    ➕ {{ getAdditions() }} additions
                  </span>
                  <span class="stat-item deletions">
                    ➖ {{ getDeletions() }} deletions
                  </span>
                </div>
              </div>

              <div class="comparison-content">
                <div
                  v-for="(categoryData, category) in comparisonResults"
                  :key="category"
                  class="comparison-category"
                >
                  <h3>{{ category }}</h3>
                  <div
                    v-for="(results, file) in categoryData"
                    :key="file"
                    class="comparison-file"
                  >
                    <h4>{{ file }}</h4>
                    <table class="comparison-table">
                      <thead>
                        <tr>
                          <th>Setting</th>
                          <th>Backup Value</th>
                          <th>Current Value</th>
                          <th>Match</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(result, index) in results"
                          :key="index"
                          :class="{ 'diff-row': !result.Equal }"
                        >
                          <td class="setting-name">{{ result.Name }}</td>
                          <td class="value-cell">
                            {{ formatValue(result.BackupValue) }}
                          </td>
                          <td class="value-cell">
                            {{ formatValue(result.CurrentValue) }}
                          </td>
                          <td>
                            <span
                              :class="[
                                'match-badge',
                                result.Equal ? 'match' : 'diff',
                              ]"
                            >
                              {{ result.Equal ? "✓" : "✗" }}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button @click="closeComparison" class="btn btn-secondary">
                Close
              </button>
              <button
                @click="restoreBackup(currentBackup)"
                class="btn btn-primary"
              >
                Restore This Backup
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import {
  fetchClients,
  fetchClientBackups,
  fetchBackupContent,
  restoreFromBackup,
  runPipeline,
  fetchAndCompareAllFilesv6,
} from "@/azure-devops-api";
import { msalInstance } from "@/auth";
import { mapState } from "vuex";
import { formatDate } from "@/services/date-utils.js";

export default {
  name: "TenantBackup",
  components: {},
  data() {
    return {
      clients: [],
      selectedClient: "",
      backups: [],
      isBackingUp: false,
      showBackupDetails: false,
      showComparison: false,
      currentBackup: null,
      backupContent: "",
      comparisonResults: {},
      projectId: "1b238677-4a8d-4243-827e-88a0558c085a",
      repoId: "62b297c6-760c-4250-a926-52b8850e6d5f",
      searchFilter: "",
      dateFilter: "all",
      currentPage: 1,
      itemsPerPage: 10,
      isLoading: false,
    };
  },
  computed: {
    ...mapState(["isLoggedIn"]),
    filteredBackups() {
      let filtered = [...this.backups];

      // Apply search filter
      if (this.searchFilter) {
        const search = this.searchFilter.toLowerCase();
        filtered = filtered.filter(
          (backup) =>
            backup.tagName.toLowerCase().includes(search) ||
            this.formatDate(backup.date).toLowerCase().includes(search)
        );
      }

      // Apply date filter
      switch (this.dateFilter) {
        case "today":
          filtered = filtered.filter((b) => this.isToday(b.date));
          break;
        case "week":
          filtered = filtered.filter((b) => this.isWithinDays(b.date, 7));
          break;
        case "month":
          filtered = filtered.filter((b) => this.isWithinDays(b.date, 30));
          break;
      }

      return filtered;
    },

    paginatedBackups() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredBackups.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredBackups.length / this.itemsPerPage);
    },
  },

  async mounted() {
    await this.loadClients();
  },

  methods: {
    async loadClients() {
      try {
        const accounts = msalInstance.getAllAccounts();
        if (!accounts.length) {
          throw new Error("No authenticated account found");
        }

        const accessToken = await msalInstance.acquireTokenSilent({
          account: accounts[0],
          scopes: ["499b84ac-1321-427f-aa17-267ca6975798/user_impersonation"],
        });

        this.clients = await fetchClients(
          accessToken.accessToken,
          this.repoId,
          "/Clients"
        );
      } catch (error) {
        console.error("Error loading clients:", error);
      }
    },

    async loadClientBackups() {
      if (!this.selectedClient) return;

      this.isLoading = true;
      try {
        const accounts = msalInstance.getAllAccounts();
        if (!accounts.length) {
          throw new Error("No authenticated account found");
        }

        const accessToken = await msalInstance.acquireTokenSilent({
          account: accounts[0],
          scopes: ["499b84ac-1321-427f-aa17-267ca6975798/user_impersonation"],
        });

        this.backups = await fetchClientBackups(
          accessToken.accessToken,
          this.projectId,
          this.selectedClient
        );

        // Reset pagination
        this.currentPage = 1;
      } catch (error) {
        console.error("Error loading backups:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async triggerManualBackup() {
      this.isBackingUp = true;
      try {
        const accounts = msalInstance.getAllAccounts();
        if (!accounts.length) {
          throw new Error("No authenticated account found");
        }

        const accessToken = await msalInstance.acquireTokenSilent({
          account: accounts[0],
          scopes: ["499b84ac-1321-427f-aa17-267ca6975798/user_impersonation"],
        });

        // Run the backup pipeline
        await runPipeline(
          accessToken.accessToken,
          this.projectId,
          "212", // Replace with actual pipeline ID
          {
            ClientCode: this.selectedClient,
            ManualRun: true,
          }
        );

        // Show success message
        console.log("Backup triggered successfully");

        // Refresh backup list after a delay
        setTimeout(() => {
          this.loadClientBackups();
        }, 5000);
      } catch (error) {
        console.error("Error triggering backup:", error);
      } finally {
        this.isBackingUp = false;
      }
    },

    async viewBackup(backup) {
      try {
        const accounts = msalInstance.getAllAccounts();
        if (!accounts.length) {
          throw new Error("No authenticated account found");
        }

        const accessToken = await msalInstance.acquireTokenSilent({
          account: accounts[0],
          scopes: ["499b84ac-1321-427f-aa17-267ca6975798/user_impersonation"],
        });

        const content = await fetchBackupContent(
          accessToken.accessToken,
          this.projectId,
          this.repoId,
          backup.tagName
        );

        this.currentBackup = backup;
        this.backupContent = JSON.stringify(content, null, 2);
        this.showBackupDetails = true;
      } catch (error) {
        console.error("Error viewing backup:", error);
      }
    },

    async compareWithCurrent(backup) {
      try {
        const accounts = msalInstance.getAllAccounts();
        if (!accounts.length) {
          throw new Error("No authenticated account found");
        }

        const accessToken = await msalInstance.acquireTokenSilent({
          account: accounts[0],
          scopes: ["499b84ac-1321-427f-aa17-267ca6975798/user_impersonation"],
        });

        this.currentBackup = backup;
        this.comparisonResults = await fetchAndCompareAllFilesv6(
          accessToken.accessToken,
          this.repoId,
          `/Clients/${this.selectedClient}@${backup.tagName}`,
          `/Clients/${this.selectedClient}`,
          [],
          (progress) => console.log(`Comparison progress: ${progress}%`),
          []
        );

        this.showComparison = true;
      } catch (error) {
        console.error("Error comparing configurations:", error);
      }
    },

    async restoreBackup(backup) {
      if (
        !confirm(
          `Are you sure you want to restore from backup ${backup.tagName}? This will create a pull request for review.`
        )
      ) {
        return;
      }

      try {
        const accounts = msalInstance.getAllAccounts();
        if (!accounts.length) {
          throw new Error("No authenticated account found");
        }

        const accessToken = await msalInstance.acquireTokenSilent({
          account: accounts[0],
          scopes: ["499b84ac-1321-427f-aa17-267ca6975798/user_impersonation"],
        });

        const pr = await restoreFromBackup(
          accessToken.accessToken,
          this.projectId,
          this.repoId,
          backup.tagName,
          this.selectedClient
        );

        // Show success message
        console.log(`Pull request #${pr.pullRequestId} created for restore`);
        alert(`Pull request #${pr.pullRequestId} has been created for review.`);
      } catch (error) {
        console.error("Error restoring backup:", error);
        alert("Failed to restore backup. Please try again.");
      }
    },

    downloadBackup(backup) {
      // Implement download functionality
      console.log("Download backup:", backup);
    },

    refreshBackups() {
      this.loadClientBackups();
    },

    configureSchedule() {
      this.$router.push({
        name: "ClientPipelineSchedules",
        query: { client: this.selectedClient },
      });
    },

    closeComparison() {
      this.showComparison = false;
      this.comparisonResults = {};
    },

    // Helper methods
    getLatestBackupDate() {
      if (this.backups.length === 0) return "N/A";
      const latest = this.backups[0];
      return this.formatDate(latest.date);
    },

    getStorageSize() {
      // This would calculate actual storage size
      return "2.3 GB";
    },

    getBackupStatus() {
      if (this.isBackingUp) return "Running";
      if (this.backups.length === 0) return "No Backups";

      const latest = this.backups[0];
      const hoursSinceBackup =
        (new Date() - new Date(latest.date)) / (1000 * 60 * 60);

      if (hoursSinceBackup < 24) return "Healthy";
      if (hoursSinceBackup < 48) return "Warning";
      return "Critical";
    },

    formatDate(date) {
      return formatDate(date, { mode: "short", locale: "en-US" });
    },

    formatTime(date) {
      return new Date(date).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    formatValue(value) {
      if (value === "Missing") return "N/A";
      if (typeof value === "object") return JSON.stringify(value);
      return value;
    },

    isToday(date) {
      const today = new Date();
      const backupDate = new Date(date);
      return backupDate.toDateString() === today.toDateString();
    },

    isWithinDays(date, days) {
      const now = new Date();
      const backupDate = new Date(date);
      const diffTime = Math.abs(now - backupDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays <= days;
    },

    getTotalChanges() {
      let total = 0;
      Object.values(this.comparisonResults).forEach((category) => {
        Object.values(category).forEach((results) => {
          total += results.filter((r) => !r.Equal).length;
        });
      });
      return total;
    },

    getAdditions() {
      let total = 0;
      Object.values(this.comparisonResults).forEach((category) => {
        Object.values(category).forEach((results) => {
          total += results.filter(
            (r) => !r.Equal && r.CurrentValue !== "Missing"
          ).length;
        });
      });
      return total;
    },

    getDeletions() {
      let total = 0;
      Object.values(this.comparisonResults).forEach((category) => {
        Object.values(category).forEach((results) => {
          total += results.filter(
            (r) => !r.Equal && r.CurrentValue === "Missing"
          ).length;
        });
      });
      return total;
    },
  },
};
</script>

<style scoped>
.backup-container {
  padding: 2rem;
  /* Remove max-width to use full screen width */
  /* max-width: 1200px; */
  /* margin: 0 auto; */
}

/* Header */
.backup-header {
  margin-bottom: 2rem;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  padding: 2rem;
  border-radius: 1rem;
  border: 1px solid #bfdbfe;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 2rem;
}

.header-text h1 {
  font-size: 2rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.header-subtitle {
  color: #64748b;
  font-size: 1rem;
}

.header-quick-stats {
  display: flex;
  gap: 2rem;
}

.quick-stat {
  text-align: center;
}

.quick-stat-value {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
  color: #3b82f6;
}

.quick-stat-label {
  display: block;
  font-size: 0.75rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 0.25rem;
}

/* Content Layout */
.backup-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Top Controls Row */
.top-controls-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  align-items: start;
}

@media (max-width: 768px) {
  .top-controls-row {
    grid-template-columns: 1fr;
  }
}

/* Selection Section */
.selection-section {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 2rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
}

/* Actions Section */
.backup-actions {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 2rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
}

/* Section Headers */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.section-header h2,
.section-header h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

/* Form Elements */
.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #475569;
  margin-bottom: 0.5rem;
}

.client-select {
  width: 100%;
  max-width: 400px;
  padding: 0.625rem 0.875rem;
  font-size: 0.875rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  background-color: #ffffff;
  color: #1e293b;
  transition: all 0.2s;
  font-family: inherit;
}

.client-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.btn {
  padding: 0.625rem 1.25rem;
  border-radius: 0.375rem;
  border: none;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-family: inherit;
}

.btn-primary {
  background-color: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.btn-secondary {
  background-color: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.btn-secondary:hover {
  background-color: #e2e8f0;
  transform: translateY(-1px);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Stats Cards */
.backup-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;
}

@media (max-width: 1200px) {
  .backup-stats {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .backup-stats {
    grid-template-columns: 1fr;
  }
}

.stat-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1.25rem;
  transition: all 0.2s;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
}

.stat-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #3b82f6, #60a5fa);
  transform: translateY(-100%);
  transition: transform 0.2s;
}

.stat-card:hover::before {
  transform: translateY(0);
}

.stat-card:hover {
  border-color: #cbd5e1;
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  font-size: 2.5rem;
  opacity: 0.9;
  background: #f0f9ff;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
}

.stat-label {
  font-size: 0.75rem;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 0.25rem;
}

/* Table Section */
.backup-table-section {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 0;
  margin-bottom: 2rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  flex-wrap: wrap;
  gap: 1rem;
}

.table-header h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.table-filters {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.search-input,
.filter-select {
  padding: 0.5rem 0.875rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background-color: #ffffff;
  color: #1e293b;
  transition: all 0.2s;
  font-family: inherit;
}

.search-input::placeholder {
  color: #94a3b8;
}

.search-input:focus,
.filter-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Table */
.backup-table-wrapper {
  overflow-x: auto;
  padding: 0 2rem 2rem 2rem;
}

.backup-table {
  width: 100%;
  border-collapse: collapse;
}

.backup-table th {
  text-align: left;
  padding: 0.75rem 1rem;
  font-weight: 600;
  color: #64748b;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background-color: #f8fafc;
}

.backup-table td {
  padding: 1rem;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
}

.backup-row:hover {
  background-color: #f8fafc;
}

.date-cell {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.date-primary {
  font-weight: 500;
  color: #1e293b;
}

.date-secondary {
  font-size: 0.75rem;
  color: #94a3b8;
}

.tag-name {
  font-family: "Monaco", "Menlo", monospace;
  background: #f1f5f9;
  padding: 0.25rem 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
  font-size: 0.8125rem;
  color: #3b82f6;
}

.backup-type {
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.backup-type.scheduled {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.backup-type.manual {
  background: rgba(251, 146, 60, 0.1);
  color: #f97316;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.status-success {
  background: rgba(34, 197, 94, 0.1);
  color: #16a34a;
}

/* Action Buttons in Table */
.action-btn {
  background: none;
  border: 1px solid #e2e8f0;
  padding: 0.375rem 0.75rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  color: #475569;
}

.action-btn:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
  color: #1e293b;
}

.action-btn.restore {
  color: #3b82f6;
  border-color: #3b82f6;
}

.action-btn.restore:hover {
  background: rgba(59, 130, 246, 0.1);
}

/* Loading and Empty States */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  color: #94a3b8;
  font-size: 1rem;
  gap: 1rem;
}

.loader {
  border: 2px solid #f1f5f9;
  border-radius: 50%;
  border-top: 2px solid #3b82f6;
  width: 32px;
  height: 32px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
}

.page-btn {
  padding: 0.5rem 1rem;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  color: #475569;
}

.page-btn:hover:not(:disabled) {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 0.875rem;
  color: #64748b;
}

/* Modals */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 2rem;
}

.modal-content {
  background: #ffffff;
  border-radius: 0.75rem;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.modal-content.modal-wide {
  max-width: 1200px;
}

.modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f8fafc;
  border-radius: 0.75rem 0.75rem 0 0;
}

.modal-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #94a3b8;
  padding: 0.25rem;
  border-radius: 0.375rem;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.close-button:hover {
  background: #e2e8f0;
  color: #475569;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  background-color: #ffffff;
}

.modal-footer {
  padding: 1.5rem;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  background-color: #f8fafc;
  border-radius: 0 0 0.75rem 0.75rem;
}

/* Detail Sections */
.detail-section {
  margin-bottom: 2rem;
}

.detail-section h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 1rem;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.detail-item {
  padding: 1rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
}

.detail-item label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.25rem;
}

.detail-item code {
  color: #3b82f6;
}

.detail-item span {
  color: #1e293b;
}

.json-preview {
  background: #f8fafc;
  color: #1e293b;
  padding: 1rem;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
  overflow-x: auto;
  max-height: 400px;
  font-size: 0.8125rem;
  line-height: 1.5;
  font-family: "Monaco", "Menlo", monospace;
}

/* Comparison Modal */
.comparison-header {
  background: #f8fafc;
  padding: 1rem 1.5rem;
  border-radius: 0.5rem;
  margin-bottom: 2rem;
  border: 1px solid #e2e8f0;
}

.comparison-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
}

.comparison-label {
  font-weight: 500;
  color: #64748b;
}

.comparison-vs {
  color: #94a3b8;
  font-style: italic;
}

.comparison-current {
  font-weight: 500;
  color: #1e293b;
}

.comparison-stats {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.stat-item {
  font-size: 0.875rem;
  font-weight: 500;
}

.stat-item.changes {
  color: #3b82f6;
}

.stat-item.additions {
  color: #16a34a;
}

.stat-item.deletions {
  color: #ef4444;
}

/* Comparison Content */
.comparison-category {
  margin-bottom: 2rem;
}

.comparison-category h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 1rem;
}

.comparison-file {
  margin-bottom: 1.5rem;
}

.comparison-file h4 {
  font-size: 0.875rem;
  font-weight: 600;
  color: #475569;
  margin-bottom: 0.5rem;
}

.comparison-table {
  width: 100%;
  border-collapse: collapse;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  overflow: hidden;
}

.comparison-table th {
  text-align: left;
  padding: 0.75rem;
  font-weight: 600;
  color: #64748b;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.comparison-table td {
  padding: 0.75rem;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.875rem;
}

.setting-name {
  font-weight: 500;
  color: #475569;
}

.value-cell {
  font-family: "Monaco", "Menlo", monospace;
  font-size: 0.8125rem;
  color: #1e293b;
}

.diff-row {
  background-color: #fef3c7;
}

.match-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 0.75rem;
  font-weight: 600;
}

.match-badge.match {
  background: rgba(34, 197, 94, 0.1);
  color: #16a34a;
}

.match-badge.diff {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

/* Modal Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-content,
.modal-leave-active .modal-content {
  transition: transform 0.3s ease;
}

.modal-enter-from .modal-content,
.modal-leave-to .modal-content {
  transform: scale(0.95);
}
</style>
